import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import PrimaryButton from 'components/atoms/primary-button/PrimaryButton';
import { createRef, useEffect, useRef } from 'react';
import type { ReactElement, RefObject } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import type { BlackholeCtaProps } from './BlackholeCta.data';
import styles from './BlackholeCta.module.scss';
import Heading from '../../atoms/heading/Heading';
import type { CtaRefType } from '../../../util/cta/cta.data';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './BlackholeCta.transitions';
import BackgroundPinwheel from '../header-rotating-shape/BackgroundPinwheel';
import type { BackgroundPinwheelRef } from '../header-rotating-shape/BackgroundPinwheel';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';
import { useScrollProviderContext } from '@/components/unlisted/ScrollProvider';
import { createScrollTrigger } from '@/util/scroll/scrollTrigger.utils';

export default function BlackholeCta({ ref, blok, ...props }: BlackholeCtaProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  // const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const pinwheelRef = useRef<BackgroundPinwheelRef>(null);
  // const blackHoleCanvasRef = useRef<BlackholeCanvas>();
  const buttons = useRef(blok?.buttons.map(() => createRef<CtaRefType>()));
  const scrollContext = useScrollProviderContext();

  // useEffect(() => {
  //   if (canvasRef) {
  //     blackHoleCanvasRef.current = new BlackholeCanvas(canvasRef);
  //   }
  //   return () => {
  //     blackHoleCanvasRef?.current?.dispose();
  //   };
  // }, [canvasRef]);

  useScrollTransition({
    scroll: {
      enableInView: true,
      canTransitionIn: props.canTransitionIn,
      trigger: elementRef,
      reverseOnLeaveBack: true,
      vars: {
        start: 'top 40%',
      },
    },
    setupOptions: () => ({
      refs: {
        buttons,
        titleRef,
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
  });

  useEffect(() => {
    if (!elementRef.current || !pinwheelRef.current?.revealTimelineRef.current) return undefined;

    const scrollTrigger = createScrollTrigger(
      elementRef.current,
      pinwheelRef.current.revealTimelineRef.current,
      {
        scroller: scrollContext?.hasSmoothScroll?.current
          ? scrollContext?.scrollWrapperRef?.current || undefined
          : undefined,
        scrub: true,
        start: 'center-=20% bottom',
        end: 'bottom bottom',
      },
    );

    return () => {
      scrollTrigger.kill();
    };
  }, [elementRef, scrollContext]);

  const pinwheelMouseDownHandler = () => {
    pinwheelRef.current?.setRotationDuration(0.25);
  };
  const pinwheelMouseUpHandler = () => {
    pinwheelRef.current?.setRotationDuration(180);
  };

  useThemeScrollBlock(wrapperRef, blok.use_accent_color, {
    foreground: blok.accent_color_foreground?.color,
    background: blok.accent_color_background?.color,
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.blackholeCta, props.className)}
      id="BlackholeCta"
      {...sbEditable(blok)}
    >
      <div ref={wrapperRef} className={classNames(styles.wrapper)}>
        <div
          className={classNames(styles.canvasContainer)}
          onMouseDown={pinwheelMouseDownHandler}
          onMouseUp={pinwheelMouseUpHandler}
          onMouseLeave={pinwheelMouseUpHandler}
        >
          <BackgroundPinwheel
            ref={pinwheelRef}
            iconColor={blok?.icon_color?.color}
            position={{ top: '50%', left: '50%' }}
            revealDelay={0}
          />
        </div>

        <Heading
          ref={titleRef}
          as="h3"
          size="heading-02"
          className={styles.title}
          disableTriggerInOnScroll
        >
          <>
            {blok.line1}
            <br />
            {blok.line2}
          </>
        </Heading>

        <div className={classNames(styles.buttonHolder)}>
          {blok?.buttons &&
            blok?.buttons?.map((data, index) => (
              <PrimaryButton
                ref={buttons.current[index]}
                key={data.title}
                className={classNames(styles.button)}
                link={{ href: data?.link?.cached_url }}
                label={data.title}
                disableTriggerInOnScroll
              />
            ))}
        </div>
      </div>
    </div>
  );
}
